export const BUDGET_ERROR_CODES = {
  INSUFFICIENT_COMPANY_BALANCE: 'INSUFFICIENT_COMPANY_BALANCE',
  INSUFFICIENT_PROGRAM_BUDGET: 'INSUFFICIENT_PROGRAM_BUDGET',
  INSUFFICIENT_FUNDS: 'INSUFFICIENT_FUNDS',
  BUDGET_EXCEEDED: 'BUDGET_EXCEEDED',
  NO_LEDGER: 'NO_LEDGER',
};

const BUDGET_ERROR_MESSAGES = {
  [BUDGET_ERROR_CODES.INSUFFICIENT_COMPANY_BALANCE]:
    "Your company's balance is insufficient to send these awards. Add funds in Settings.",
  [BUDGET_ERROR_CODES.INSUFFICIENT_FUNDS]:
    "Your company's balance is insufficient to send these awards. Add funds in Settings.",
  [BUDGET_ERROR_CODES.INSUFFICIENT_PROGRAM_BUDGET]: "This award would exceed the award's budget for this period.",
  [BUDGET_ERROR_CODES.BUDGET_EXCEEDED]: "This award would exceed the award's budget for this period.",
  [BUDGET_ERROR_CODES.NO_LEDGER]: 'This award has no funding account, so it cannot carry a reward.',
};

export function budgetErrorMessage(errorCode) {
  return BUDGET_ERROR_MESSAGES[errorCode] || null;
}

export function validationErrorMessage(validation) {
  if (!validation || validation.is_valid) {
    return null;
  }
  return budgetErrorMessage(validation.error_code);
}

// Mirrors the server's validate_budget_constraints check order: zero amounts are
// free, then company balance, then the program's period budget. budget_spent_minor
// arrives from the programs list already scoped to the current period, so the period
// and financial-year boundaries are never recomputed here.
export function evaluateBudget({ program, companyBalanceMinor, amountMinor, recipientCount }) {
  const totalMinor = (amountMinor || 0) * (recipientCount || 0);
  const programBudgetMinor = program?.budget_minor ?? null;
  const hasPeriodBudget = programBudgetMinor !== null && !!program?.properties?.budget_period;
  const spentMinor = program?.budget_spent_minor ?? 0;
  const remainingMinor = hasPeriodBudget ? programBudgetMinor - spentMinor : null;

  const result = {
    is_valid: true,
    error_code: '',
    company_balance_minor: companyBalanceMinor ?? 0,
    company_balance_after_grant_minor: (companyBalanceMinor ?? 0) - totalMinor,
    program_budget_minor: hasPeriodBudget ? programBudgetMinor : null,
    program_budget_spent_minor: hasPeriodBudget ? spentMinor : null,
    program_budget_remaining_minor: remainingMinor,
    program_budget_remaining_after_grant_minor: hasPeriodBudget ? remainingMinor - totalMinor : null,
  };

  if (totalMinor === 0) {
    return result;
  }

  if (!program?.ledger_id) {
    return { ...result, is_valid: false, error_code: BUDGET_ERROR_CODES.NO_LEDGER };
  }

  if (companyBalanceMinor === null || companyBalanceMinor === undefined) {
    return result;
  }

  if (totalMinor > companyBalanceMinor) {
    return { ...result, is_valid: false, error_code: BUDGET_ERROR_CODES.INSUFFICIENT_COMPANY_BALANCE };
  }

  if (hasPeriodBudget && totalMinor > remainingMinor) {
    return { ...result, is_valid: false, error_code: BUDGET_ERROR_CODES.INSUFFICIENT_PROGRAM_BUDGET };
  }

  return result;
}

export function buildAwardPayload({
  programId,
  nominatorEmployeeId,
  employees,
  amountMinor,
  recipientMessage,
  companyValues,
  category,
  isPrivate,
}) {
  return {
    recognition_award_program_id: programId,
    nominator_employee_id: nominatorEmployeeId || undefined,
    nominee_employee_id: employees.map(e => e.employee_id),
    amount_minor: amountMinor ?? null,
    recipient_message: recipientMessage || null,
    company_values: companyValues.length > 0 ? companyValues : null,
    category: category || null,
    scope: isPrivate ? { type: 'employee' } : undefined,
  };
}
